import React, { useEffect } from 'react';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { HelmetProvider } from 'react-helmet-async';
import { theme } from './theme';
import { PersonalInfoProvider } from './contexts/PersonalInfoContext';
import { CompanyInfoProvider } from './contexts/CompanyInfoContext';
import { DesignSettingsProvider } from './contexts/DesignSettingsContext';
import { DesignProvider } from './context/DesignContext';
import { ImpersonationProvider, useImpersonation } from './contexts/ImpersonationContext';
import { ImpersonationBanner } from './components/ImpersonationBanner';
import AppRoutes from './AppRoutes';
import { Header } from './components/Header';
import { ClerkProvider, useUser } from '@clerk/clerk-react';
import Footer from './components/Footer';
import { ScrollToTop } from './components/ScrollToTop';
import { usePaymentSuccess } from './hooks/usePaymentSuccess';
import { Alert, Snackbar } from '@mui/material';
import { PreloadedBannerService } from './services/preloadedBannerService';
import './styles/buttonStyles.css';
import { initGA, setUserId } from './utils/googleAnalytics';
import { AdminTierService } from './services/adminTierService';
import { useUserInit } from './hooks/useUserInit';
import { MarketingConsentModal } from './components/MarketingConsentModal';

const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!clerkPubKey) {
  throw new Error("Missing Clerk Publishable Key");
}

// Create a cache for emotion
const cache = createCache({
  key: 'css',
  prepend: true,
});

// Authenticated content wrapper - only renders when Clerk is available
const AuthenticatedContent = () => {
  const location = useLocation();
  const isBusinessCard = location.pathname.startsWith('/card/');
  
  // On public card pages, skip all Clerk-related logic
  if (isBusinessCard) {
    return null;
  }

  // Only call Clerk hooks when NOT on a public card page (ClerkProvider is available)
  const { user, isSignedIn } = useUser();
  useUserInit();

  // Track user ID in Google Analytics when signed in
  useEffect(() => {
    if (isSignedIn && user?.id) {
      try {
        setUserId(user.id);
        console.log('📊 GA4: User ID set:', user.id);
      } catch (error) {
        console.warn('Failed to set GA4 user ID:', error);
      }
    }
  }, [isSignedIn, user?.id]);

  // Preload banner cache on app startup (only for authenticated pages)
  useEffect(() => {
    console.log('🚨 BANNER_EXPORT_DEBUG - App.tsx - Calling preloadCache...');
    PreloadedBannerService.preloadCache().catch(error => {
      // Silently fail for storage errors (common in sandboxed contexts)
      const errorMessage = error?.message || error?.toString() || '';
      if (errorMessage.includes('Locks API') || errorMessage.includes('localStorage') || errorMessage.includes('sandboxed')) {
        console.warn('Banner cache preload skipped (sandboxed context)');
      } else {
        console.error('🚨 BANNER_EXPORT_DEBUG - App.tsx - Failed to preload banner cache:', error);
      }
    });
  }, []);

  // Setup admin tier testing console commands (only for authenticated pages)
  useEffect(() => {
    try {
      AdminTierService.setupConsoleCommands();
    } catch (error) {
      console.warn('Admin tier setup failed:', error);
    }
  }, []);

  // Show marketing consent modal for signed-in users
  if (isSignedIn) {
    return <MarketingConsentModal />;
  }

  return null; // This component only handles side effects
};

const AppContent = () => {
  const location = useLocation();
  const isBusinessCard = location.pathname.startsWith('/card/');
  const showHeader = !isBusinessCard;
  const showFooter = !location.pathname.startsWith('/generator') && !location.pathname.startsWith('/analytics') && !isBusinessCard;
  const { isProcessing, result, clearResult } = usePaymentSuccess();
  const { impersonationState } = useImpersonation();
  
  // Calculate top padding: header (64px) + impersonation banner (48px if impersonating)
  const isImpersonating = impersonationState.isImpersonating && impersonationState.impersonatedUser;
  const topPadding = showHeader ? (isImpersonating ? '112px' : '64px') : '0px';

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {showHeader && <Header />}
      <ImpersonationBanner />
      <div className="flex-grow" style={{ paddingTop: topPadding }}>
        <div className="min-h-screen bg-white">
          <main>
            <div className="min-h-screen bg-white">
              <div className={isBusinessCard ? "w-full" : "w-full px-4 sm:px-6 lg:px-8 py-12"}>
                <AppRoutes />
              </div>
            </div>
          </main>
        </div>
      </div>
      {showFooter && <Footer />}
      {result && (
        <Snackbar
          open={!!result}
          autoHideDuration={6000}
          onClose={clearResult}
        >
          <Alert 
            onClose={clearResult} 
            severity={result.success ? "success" : "error"}
          >
            {result.message}
          </Alert>
        </Snackbar>
      )}
    </div>
  );
};

// Component that conditionally wraps with ClerkProvider
const ConditionalClerkProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Check synchronously during render (not in useEffect) to prevent ClerkProvider from initializing
  const isPublicPage = typeof window !== 'undefined' && window.location.pathname.startsWith('/card/');

  // On public pages, skip ClerkProvider to avoid localStorage errors
  if (isPublicPage) {
    return <>{children}</>;
  }

  // On authenticated pages, wrap with ClerkProvider
  return (
      <ClerkProvider 
        publishableKey={clerkPubKey}
        afterSignInUrl="/free"
        afterSignUpUrl="/free"
        // Explicitly prevent Clerk from redirecting /accept-invitation
        signInFallbackRedirectUrl={typeof window !== 'undefined' && window.location.pathname === '/accept-invitation' 
          ? window.location.pathname + window.location.search 
          : undefined}
        signUpFallbackRedirectUrl={typeof window !== 'undefined' && window.location.pathname === '/accept-invitation'
          ? window.location.pathname + window.location.search 
          : undefined}
        appearance={{
          baseTheme: undefined,
          elements: {
            formButtonPrimary: 'bg-[#e11d48] hover:bg-[#be123c]',
            footerActionLink: 'text-[#e11d48] hover:text-[#be123c]',
          },
          layout: {
            socialButtonsVariant: 'iconButton',
            logoPlacement: 'inside',
          },
        }}
        localization={{
          signIn: {
            start: {
              title: 'Welcome back',
              subtitle: 'Sign in to your account',
            }
          },
          signUp: {
            start: {
              title: 'Create your account',
              subtitle: 'Sign up to get started',
            }
          }
        }}
      >
      {children}
    </ClerkProvider>
  );
};

function App() {
  // Initialize Google Analytics on app load
  useEffect(() => {
    initGA();
  }, []);

  return (
    <HelmetProvider>
      <ConditionalClerkProvider>
        <CacheProvider value={cache}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <ScrollToTop />
              <ImpersonationProvider>
                <PersonalInfoProvider>
                  <CompanyInfoProvider>
                    <DesignSettingsProvider>
                      <DesignProvider>
                        <AuthenticatedContent />
                        <AppContent />
                      </DesignProvider>
                    </DesignSettingsProvider>
                  </CompanyInfoProvider>
                </PersonalInfoProvider>
              </ImpersonationProvider>
            </Router>
          </ThemeProvider>
        </CacheProvider>
      </ConditionalClerkProvider>
    </HelmetProvider>
  );
}

export default App;
